import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {PosterObject, ResizeDimensions} from '@PosterWhiteboard/poster/poster.types';
import type {PosterTypeDimensions, PosterTypeObject} from '@PosterWhiteboard/poster-type/poster-type.types';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {ItemCropObject} from '@PosterWhiteboard/classes/item-crop.class';
import {resizeColorBackgroundItemObject} from '@PosterWhiteboard/page/background/background-color.util';
import {FULL_PAGE_OFFSET, FULL_PAGE_OVERFLOW} from '@PosterWhiteboard/page/page.types';

interface DimesionsAndCoordinates {
  width: number;
  height: number;
  x: number;
  y: number;
}

// Structural type covering the fields the crop-math helpers need. Both
// ImageItemObject and VideoItemObject satisfy it — they both crop a
// source media file to fit the canvas. loadedImageWidth/Height is optional
// because video items don't carry it (their `width`/`height` are the
// intrinsic video dims).
interface CroppableBackgroundObject {
  cropData: ItemCropObject;
  width: number;
  height: number;
  loadedImageWidth?: number;
  loadedImageHeight?: number;
}

export class ResizePoster extends CommonMethods {
  public poster: Poster;

  constructor(poster: Poster) {
    super();
    this.poster = poster;
  }

  public getResizedPosterObjectForTemplate(posterType: PosterTypeObject): PosterObject {
    const updatedPoster = this.poster.toObject();

    const currentWidth = updatedPoster.width;
    const currentHeight = updatedPoster.height;

    if (!(currentWidth === posterType.width && currentHeight === posterType.height)) {
      for (const pageKey of Object.keys(updatedPoster.pages.pagesHashMap)) {
        const page = updatedPoster.pages.pagesHashMap[pageKey];
        for (const [, itemObject] of Object.entries(updatedPoster.pages.pagesHashMap[pageKey].items.itemsHashMap)) {
          if (itemObject.isBackground) {
            continue;
          }
          const resizeDimensions = this.getDimensionsToResizePageItemsForTemplate(updatedPoster.width, updatedPoster.height, itemObject, {
            width: posterType.width,
            height: posterType.height,
          });
          itemObject.left += resizeDimensions.left;
          itemObject.top += resizeDimensions.top;
          itemObject.scaleX *= resizeDimensions.scaleFactor;
          itemObject.scaleY *= resizeDimensions.scaleFactor;
        }

        this.resizePageBackgroundsForNewSize(page, posterType);
      }

      updatedPoster.width = posterType.width;
      updatedPoster.height = posterType.height;
    }
    updatedPoster.type = posterType;
    return updatedPoster;
  }

  public getDimensionsToResizePageItemsForTemplate(width: number, height: number, item: ItemObject, posterTypeDimensions: PosterTypeDimensions): ResizeDimensions {
    const currentAspectRatio = width / height;
    const params = getParametersForCoverResizing(posterTypeDimensions.width, posterTypeDimensions.height, currentAspectRatio);
    const scaleFactor = params.height / height;
    const resizeXAdjust = width - params.width;
    const resizeYAdjust = height - params.height;

    return {
      left: Math.round(params.x - (item.left / width) * resizeXAdjust),
      top: Math.round(params.y - (item.top / height) * resizeYAdjust),
      scaleFactor,
    };
  }

  public getDimensionsToResizePageItemsToTargetType(width: number, height: number, item: ItemObject, posterTypeDimensions: PosterTypeDimensions): ResizeDimensions {
    const currentWidth = width;
    const currentHeight = height;
    const currentAspectRatio = currentWidth / currentHeight;

    const params = getParametersForResizing(posterTypeDimensions.width, posterTypeDimensions.height, currentAspectRatio);
    const scaleFactor = params.height / currentHeight;
    const resizeXAdjust = currentWidth - params.width;
    const resizeYAdjust = currentHeight - params.height;

    return {
      left: Math.floor(params.x - (item.left / currentWidth) * resizeXAdjust),
      top: Math.floor(params.y - (item.top / currentHeight) * resizeYAdjust),
      scaleFactor,
    };
  }

  public getResizedPosterObjectForPosterType(posterType: PosterTypeObject): PosterObject {
    const updatedPoster = this.poster.toObject();

    const currentWidth = updatedPoster.width;
    const currentHeight = updatedPoster.height;

    if (!(currentWidth === posterType.width && currentHeight === posterType.height)) {
      for (const pageKey of Object.keys(updatedPoster.pages.pagesHashMap)) {
        const page = updatedPoster.pages.pagesHashMap[pageKey];
        for (const [, itemObject] of Object.entries(updatedPoster.pages.pagesHashMap[pageKey].items.itemsHashMap)) {
          if (itemObject.isBackground) {
            continue;
          }
          const resizeDimensions = this.getDimensionsToResizePageItemsToTargetType(updatedPoster.width, updatedPoster.height, itemObject, {
            width: posterType.width,
            height: posterType.height,
          });
          itemObject.left += resizeDimensions.left;
          itemObject.top += resizeDimensions.top;
          itemObject.scaleX *= resizeDimensions.scaleFactor;
          itemObject.scaleY *= resizeDimensions.scaleFactor;
        }

        this.resizePageBackgroundsForNewSize(page, posterType);
      }

      updatedPoster.width = posterType.width;
      updatedPoster.height = posterType.height;
    }
    updatedPoster.type = posterType;
    return updatedPoster;
  }

  public resizePagesForAddedDesign(pagesHashMap: Record<string, PageObject>, fromWidth: number, fromHeight: number, toWidth: number, toHeight: number): void {
    if (fromWidth === toWidth && fromHeight === toHeight) {
      return;
    }
    const targetDimensions = {width: toWidth, height: toHeight} as PosterTypeObject;

    for (const pageKey of Object.keys(pagesHashMap)) {
      const page = pagesHashMap[pageKey];
      for (const [, itemObject] of Object.entries(page.items.itemsHashMap)) {
        if (itemObject.isBackground) {
          continue;
        }
        const resizeDimensions = this.getDimensionsToResizePageItemsToTargetType(fromWidth, fromHeight, itemObject, {width: toWidth, height: toHeight});
        itemObject.left += resizeDimensions.left;
        itemObject.top += resizeDimensions.top;
        itemObject.scaleX *= resizeDimensions.scaleFactor;
        itemObject.scaleY *= resizeDimensions.scaleFactor;
      }

      this.resizePageBackgroundsForNewSize(page, targetDimensions);
    }
  }

  private resizePageBackgroundsForNewSize(page: PageObject, newPoster: PosterTypeDimensions): void {
    for (const itemId of page.items.itemsOrder) {
      const item = page.items.itemsHashMap[itemId];
      if (!item?.isBackground) {
        continue;
      }
      resizeBackgroundItemObjectForPoster(item, newPoster);
    }
  }
}

// Refits a serialized background to a poster of different dimensions. Color bgs
// are re-stretched to the new canvas; image/video bgs re-pick a crop matching
// the new aspect ratio. Used both when resizing a poster and when pasting a bg
// copied from a poster with other dimensions.
export const resizeBackgroundItemObjectForPoster = (item: ItemObject, newPoster: PosterTypeDimensions): void => {
  if (item.gitype === ITEM_TYPE.RECTANGLE) {
    resizeColorBackgroundItemObject(item as RectangleItemObject, newPoster.width, newPoster.height);
    return;
  }

  if (item.gitype === ITEM_TYPE.IMAGE) {
    // Re-pick a crop matching the new canvas aspect ratio. Expands the existing crop
    // when the new aspect allows it (so resizing back to the original poster size
    // recovers the original framing instead of staying narrowed). Scale + position
    // are applied by ImageItem.updateFabricObject()'s background fill-page pass after reinit.
    adjustCropDataForNewAspectRatio(item as ImageItemObject, newPoster);
    return;
  }

  if (item.gitype === ITEM_TYPE.VIDEO) {
    // Same crop-expansion treatment as image bg. VideoItem has no isBackground-aware
    // fabric override, so set position + scale explicitly from the new crop dims.
    resizeVideoBackground(item as VideoItemObject, newPoster);
  }
};

/**
 * Recomputes the crop region for a new poster aspect ratio. Unlike a strict "shrink-to-fit"
 * which keeps narrowing on each resize, this expands one dimension of the existing crop
 * whenever the new aspect allows it — so resizing back to the original poster size recovers
 * the original framing. The crop is centered on the existing crop's center and clamped to
 * the source media bounds (cropData.imageWidth × cropData.imageHeight).
 */
const adjustCropDataForNewAspectRatio = (item: CroppableBackgroundObject, newPoster: PosterTypeDimensions): void => {
  // Ensure source-image reference dims are set (the cropper writes them, but defensively
  // fall back to the loaded media dims if missing — getScaledCropData would divide by 0).
  if (!item.cropData.imageWidth || !item.cropData.imageHeight) {
    const loadedImageWidth = item.loadedImageWidth ?? 0;
    const loadedImageHeight = item.loadedImageHeight ?? 0;
    item.cropData.imageWidth = loadedImageWidth > 0 ? loadedImageWidth : item.width;
    item.cropData.imageHeight = loadedImageHeight > 0 ? loadedImageHeight : item.height;
  }
  const sourceW = item.cropData.imageWidth;
  const sourceH = item.cropData.imageHeight;

  // Never-cropped image bgs land here with width/height = 0; treat as the full source.
  const oldW = item.cropData.width || sourceW;
  const oldH = item.cropData.height || sourceH;
  const oldX = item.cropData.width ? item.cropData.x : 0;
  const oldY = item.cropData.height ? item.cropData.y : 0;

  const targetAR = newPoster.width / newPoster.height;

  // Choose the dimension that EXPANDS (or matches) the other. `oldW / targetAR` is the
  // height needed to keep oldW at the target aspect; if it's >= oldH the height grows
  // (good — no narrowing). Otherwise keep oldH and let the width grow.
  let newW: number;
  let newH: number;
  if (oldW / targetAR >= oldH) {
    newW = oldW;
    newH = Math.floor(oldW / targetAR);
  } else {
    newH = oldH;
    newW = Math.floor(oldH * targetAR);
  }

  // Clamp to source bounds while preserving target aspect.
  if (newW > sourceW) {
    newW = sourceW;
    newH = Math.floor(sourceW / targetAR);
  }
  if (newH > sourceH) {
    newH = sourceH;
    newW = Math.floor(sourceH * targetAR);
  }

  // Center on the old crop's center, then clamp the position to the source bounds.
  const cx = oldX + oldW / 2;
  const cy = oldY + oldH / 2;
  const newX = Math.max(0, Math.min(sourceW - newW, Math.round(cx - newW / 2)));
  const newY = Math.max(0, Math.min(sourceH - newH, Math.round(cy - newH / 2)));

  item.cropData.x = newX;
  item.cropData.y = newY;
  item.cropData.width = newW;
  item.cropData.height = newH;
  item.cropData.cropped = true;
};

const resizeVideoBackground = (item: VideoItemObject, newPoster: PosterTypeDimensions): void => {
  adjustCropDataForNewAspectRatio(item, newPoster);
  // cropData.width/height now match newPoster's aspect ratio (within rounding), so
  // scaleX ≈ scaleY and the cropped region covers the canvas with FULL_PAGE_OVERFLOW.
  item.left = FULL_PAGE_OFFSET;
  item.top = FULL_PAGE_OFFSET;
  if (item.cropData.width > 0 && item.cropData.height > 0) {
    item.scaleX = (newPoster.width + FULL_PAGE_OVERFLOW) / item.cropData.width;
    item.scaleY = (newPoster.height + FULL_PAGE_OVERFLOW) / item.cropData.height;
  }
};

const getParametersForCoverResizing = (targetWidth: number, targetHeight: number, currentAspectRatio: number): DimesionsAndCoordinates => {
  let newHeight = targetHeight;
  let newWidth = targetHeight * currentAspectRatio;

  if (newWidth < targetWidth) {
    newWidth = targetWidth;
    newHeight = targetWidth / currentAspectRatio;
  }

  return {height: newHeight, width: newWidth, x: (targetWidth - newWidth) / 2, y: (targetHeight - newHeight) / 2};
};

const getParametersForResizing = (targetWidth: number, targetHeight: number, currentAspectRatio: number): DimesionsAndCoordinates => {
  let newHeight;
  let newWidth;

  newHeight = targetHeight;
  newWidth = targetHeight * currentAspectRatio;

  if (newWidth > targetWidth) {
    newWidth = targetWidth;
    newHeight = targetWidth / currentAspectRatio;
  }

  return {height: newHeight, width: newWidth, x: (targetWidth - newWidth) / 2, y: (targetHeight - newHeight) / 2};
};
